// Click-to-call, Phase 1: the company's two switches, shown in Business schedule → Calls and phone line.
//   Outbound calling — staff may press "Call" (their own phone rings first, then the client is called from the company line).
//   Record outbound calls — off by default; when on, the client hears "This call may be recorded." before connecting.
// Stored in org_outbound_call_settings (one row per company; owners/admins change it, row-level security enforces that).
import React, { useCallback, useEffect, useState } from 'react';
import Switch from '../ui/Switch';
import { D2_SECTION_TITLE } from '../settings/signatures/d2Fields';

export default function OutboundCallSettings({ orgId, client }) {
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        if (!orgId || !client?.from) return;
        setError('');
        const { data, error: loadError } = await client.from('org_outbound_call_settings')
            .select('outbound_calling_enabled, recording_enabled')
            .eq('org_id', orgId)
            .maybeSingle();
        if (loadError) { setError('Could not load the outbound calling settings.'); return; }
        setSettings({
            outbound_calling_enabled: data?.outbound_calling_enabled === true,
            recording_enabled: data?.recording_enabled === true,
        });
    }, [client, orgId]);

    useEffect(() => { load(); }, [load]);

    async function save(change) {
        const next = { ...settings, ...change };
        setBusy(true);
        setError('');
        setNotice('');
        setSettings(next);
        const { error: saveError } = await client.from('org_outbound_call_settings')
            .upsert({ org_id: orgId, ...next }, { onConflict: 'org_id' });
        setBusy(false);
        if (saveError) {
            await load();
            setError('Could not save. Only a company owner or admin can change calling settings.');
            return;
        }
        setNotice('Outbound calling settings saved.');
    }

    if (!client?.from) return null;
    if (!settings && !error) return <p className="text-sm text-[var(--ink-muted)]" aria-busy="true">Loading outbound calling…</p>;

    return (
        <div className="space-y-3 border-t border-[var(--border)] pt-4" data-testid="outbound-call-settings">
            <h4 className={D2_SECTION_TITLE}>Outbound calls</h4>
            {error && <p role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm">{error}</p>}
            {settings && (
                <>
                    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-[var(--border)] px-3 py-2">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold">Outbound calling</p>
                            <p className="text-xs text-[var(--ink-muted)]">
                                Staff can press Call in Communications and on CRM cards. Their own phone rings first; when they press 1,
                                the client is called from the company line, so the client sees the company number.
                            </p>
                        </div>
                        <Switch
                            label="Outbound calling"
                            checked={settings.outbound_calling_enabled}
                            busy={busy}
                            onChange={(on) => save({ outbound_calling_enabled: on })}
                        />
                    </div>
                    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-[var(--border)] px-3 py-2">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold">Record outbound calls</p>
                            <p className="text-xs text-[var(--ink-muted)]">
                                Off by default. When on, the client hears “This call may be recorded.” before they are connected.
                            </p>
                        </div>
                        <Switch
                            label="Record outbound calls"
                            checked={settings.recording_enabled}
                            busy={busy}
                            onChange={(on) => save({ recording_enabled: on })}
                        />
                    </div>
                </>
            )}
            {notice && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>}
        </div>
    );
}
